import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Optimistic image swap: update gallery image the moment a variant is clicked,
    // before the server section fetch completes. Reads image URL from data-variant-image
    // already rendered on each variant option (no extra network).
    target?.addEventListener('change', this.#handleOptimisticSwap, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  /**
   * Fast client-side image swap on variant change. Runs BEFORE the section-render
   * fetch completes. Finds the variant option's data-variant-image and swaps the
   * primary gallery <img> src/srcset. The server fetch still morphs the gallery
   * afterward — by then the browser has the new image cached, so the morph is
   * seamless.
   */
  #handleOptimisticSwap = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    let imageUrl = null;
    let mediaId = null;

    if (target instanceof HTMLSelectElement) {
      const opt = target.options[target.selectedIndex];
      imageUrl = opt?.dataset.variantImage || null;
      mediaId = opt?.dataset.variantImageId || null;
    } else if (target instanceof HTMLInputElement && target.type === 'radio') {
      imageUrl = target.dataset.variantImage || null;
      mediaId = target.dataset.variantImageId || null;
    } else {
      return;
    }

    if (!imageUrl) return;

    // Pick the primary (first visible) image in this gallery.
    const primary = this.querySelector('.product-media-container img, [ref="media[]"] img, img');
    if (!(primary instanceof HTMLImageElement)) return;

    // If this image already matches (same media id), skip — avoids flicker when
    // the morph lands first.
    if (mediaId && primary.dataset.mediaId === mediaId) return;

    // Build a responsive srcset from Shopify's image URL by swapping the width param.
    const makeUrl = (w) => imageUrl.replace(/([?&])width=\d+/, `$1width=${w}`);
    const widths = [400, 600, 800, 1200, 1600];
    const srcset = widths.map((w) => `${makeUrl(w)} ${w}w`).join(', ');

    // Swap: browser starts fetching new image immediately.
    primary.srcset = srcset;
    primary.src = makeUrl(1200);
    if (mediaId) primary.dataset.mediaId = mediaId;
  };

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
